import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { API } from '../config';

const VehicleContext = createContext();

export const useVehicles = () => {
  const context = useContext(VehicleContext);
  if (!context) {
    throw new Error('useVehicles must be used within VehicleProvider');
  }
  return context;
};

export const VehicleProvider = ({ children }) => {
  const { customer, isAuthenticated, getAuthHeaders } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [syncing, setSyncing] = useState(false);

  // Load favorites from backend or localStorage on mount
  useEffect(() => {
    loadFavorites();
    loadCompareList();
  }, [customer]);

  const loadFavorites = async () => {
    if (isAuthenticated()) {
      // Authenticated: Load from backend
      try {
        const response = await axios.get(`${API}/public/customer/favorites`, {
          headers: getAuthHeaders()
        });
        setFavorites(response.data);
        // Also sync to localStorage as backup
        localStorage.setItem('favorites', JSON.stringify(response.data));
      } catch (error) {
        console.error('Error loading favorites from backend:', error);
        // Fallback to localStorage
        const saved = localStorage.getItem('favorites');
        if (saved) {
          setFavorites(JSON.parse(saved));
        }
      }
    } else {
      // Not authenticated: Load from localStorage
      const saved = localStorage.getItem('favorites');
      if (saved) {
        setFavorites(JSON.parse(saved));
      }
    }
  };

  const loadCompareList = () => {
    const savedCompare = localStorage.getItem('compareList');
    if (savedCompare) {
      setCompareList(JSON.parse(savedCompare));
    }
  };

  // Save compare list to localStorage
  useEffect(() => {
    localStorage.setItem('compareList', JSON.stringify(compareList));
  }, [compareList]);

  // Sync favorites with backend when authenticated
  const syncFavoritesWithBackend = async (vehicle, action) => {
    if (!isAuthenticated() || syncing) return;

    setSyncing(true);
    try {
      if (action === 'add') {
        await axios.post(`${API}/public/customer/favorites/${vehicle.id}`, {}, {
          headers: getAuthHeaders()
        });
      } else if (action === 'remove') {
        await axios.delete(`${API}/public/customer/favorites/${vehicle.id}`, {
          headers: getAuthHeaders()
        });
      }
    } catch (error) {
      console.error('Error syncing favorites:', error);
    } finally {
      setSyncing(false);
    }
  };

  const addToFavorites = async (vehicle) => {
    if (!favorites.find(v => v.id === vehicle.id)) {
      const newFavorites = [...favorites, vehicle];
      setFavorites(newFavorites);
      localStorage.setItem('favorites', JSON.stringify(newFavorites));
      
      // Sync with backend if authenticated
      await syncFavoritesWithBackend(vehicle, 'add');
      
      toast.success('Added to favorites!');
      return true;
    }
    return false;
  };

  const removeFromFavorites = async (vehicleId) => {
    const vehicle = favorites.find(v => v.id === vehicleId);
    const newFavorites = favorites.filter(v => v.id !== vehicleId);
    setFavorites(newFavorites);
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
    
    // Sync with backend if authenticated
    if (vehicle) {
      await syncFavoritesWithBackend(vehicle, 'remove');
    }
    
    toast.success('Removed from favorites');
  };

  const toggleFavorite = async (vehicle) => {
    if (favorites.find(v => v.id === vehicle.id)) {
      await removeFromFavorites(vehicle.id);
      return false;
    } else {
      await addToFavorites(vehicle);
      return true;
    }
  };

  const isFavorite = (vehicleId) => {
    return favorites.some(v => v.id === vehicleId);
  };

  const addToCompare = (vehicle) => {
    if (compareList.length >= 4) {
      toast.error('You can only compare up to 4 vehicles');
      return false;
    }
    if (!compareList.find(v => v.id === vehicle.id)) {
      setCompareList([...compareList, vehicle]);
      toast.success('Added to comparison!');
      return true;
    }
    return false;
  };

  const removeFromCompare = (vehicleId) => {
    setCompareList(compareList.filter(v => v.id !== vehicleId));
    toast.success('Removed from comparison');
  };

  const toggleCompare = (vehicle) => {
    if (compareList.find(v => v.id === vehicle.id)) {
      removeFromCompare(vehicle.id);
      return false;
    } else {
      return addToCompare(vehicle);
    }
  };

  const isInCompare = (vehicleId) => {
    return compareList.some(v => v.id === vehicleId);
  };

  const clearCompare = () => {
    setCompareList([]);
    toast.success('Comparison list cleared');
  };

  return (
    <VehicleContext.Provider
      value={{
        favorites,
        compareList,
        addToFavorites,
        removeFromFavorites,
        toggleFavorite,
        isFavorite,
        addToCompare,
        removeFromCompare,
        toggleCompare,
        isInCompare,
        clearCompare,
      }}
    >
      {children}
    </VehicleContext.Provider>
  );
};
